import { useTranslation } from 'react-i18next';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Checkbox } from '../ui/checkbox';
import { Loader2 } from 'lucide-react';
import ReceiptUploader from './ReceiptUploader';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * EditExpenseDialog - Dialog for editing draft expenses with full receipt management
 */
const EditExpenseDialog = ({
  open,
  onOpenChange,
  expense,
  projects,
  employees = [],
  currentEmployeeId,
  isAdminOrManager = false,
  expenseCategories,
  currencies,
  paymentMethods = [],
  onSave,
  assets = []
}) => {
  const { t } = useTranslation();
  // Form state
  const [formData, setFormData] = useState({
    project_id: '',
    category: 'travel',
    subcategory: '',
    amount: '',
    currency: 'EUR',
    expense_date: '',
    description: '',
    vendor: '',
    invoice_number: '',
    payment_method: '',
    assigned_employee_id: '',
    billable_to_customer: false,
    asset_id: ''
  });
  
  // Get team members for selected project (or all employees for non-project)
  const projectEmployees = useMemo(() => {
    if (!formData.project_id || !employees) return [];
    
    // For non-project expenses, show all employees
    if (formData.project_id === 'non-project') {
      return employees;
    }
    
    const project = projects.find(p => p.id === formData.project_id);
    if (!project?.team_members) return [];
    
    const teamMemberIds = project.team_members.map(tm => tm.employee_id);
    return employees.filter(e => teamMemberIds.includes(e.id));
  }, [formData.project_id, projects, employees]);
  
  // Receipt state
  const [existingReceipts, setExistingReceipts] = useState([]);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [loadingReceipts, setLoadingReceipts] = useState(false);
  const [saving, setSaving] = useState(false);

  // Load expense data when dialog opens
  useEffect(() => {
    if (open && expense) {
      setFormData({
        project_id: expense.project_id || '',
        category: expense.category || 'travel',
        subcategory: expense.subcategory || '',
        amount: expense.amount?.toString() || '',
        currency: expense.currency || 'EUR',
        expense_date: expense.expense_date ? expense.expense_date.split('T')[0] : '',
        description: expense.description || '',
        vendor: expense.vendor || '',
        invoice_number: expense.invoice_number || '',
        payment_method: expense.payment_method || '',
        assigned_employee_id: expense.assigned_employee_id || '',
        billable_to_customer: expense.billable_to_customer || false,
        asset_id: expense.asset_id || ''
      });
      
      // Fetch existing receipts
      fetchReceipts(expense.id);
    }
  }, [open, expense]);

  // Fetch receipts for the expense
  const fetchReceipts = async (expenseId) => {
    setLoadingReceipts(true);
    try {
      const response = await axios.get(`${API}/expenses/${expenseId}/receipts`);
      setExistingReceipts(response.data.receipts || []);
    } catch (error) {
      console.error('Error fetching receipts:', error);
      setExistingReceipts([]);
    } finally {
      setLoadingReceipts(false);
    }
  };

  // Handle form field changes
  const handleChange = (field, value) => {
    setFormData(prev => {
      const updated = { ...prev, [field]: value };
      // Reset subcategory when category changes
      if (field === 'category') {
        updated.subcategory = '';
      }
      return updated;
    });
  };

  // Get subcategories for current category
  const getSubcategories = () => {
    const cat = expenseCategories[formData.category];
    return Array.isArray(cat) ? cat : (cat?.subcategories || []);
  };

  // Get display name for category
  const getCategoryDisplayName = (categoryKey) => {
    const cat = expenseCategories[categoryKey];
    if (!cat) return categoryKey;
    if (Array.isArray(cat)) return categoryKey.charAt(0).toUpperCase() + categoryKey.slice(1);
    return cat.display_name || categoryKey;
  };

  // View a receipt
  const handleViewReceipt = async (receipt) => {
    try {
      const url = receipt.id === 'legacy'
        ? `${API}/expenses/${expense.id}/receipt`
        : `${API}/expenses/${expense.id}/receipt/${receipt.id}`;
        
      const response = await axios.get(url, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: response.headers['content-type'] });
      const blobUrl = window.URL.createObjectURL(blob);
      window.open(blobUrl, '_blank');
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 100);
    } catch (error) {
      toast.error(t('expenses.toasts.loadReceiptFailed'));
    }
  };

  // Delete a receipt
  const handleDeleteReceipt = async (receiptId) => {
    if (!window.confirm(t('auto.misc2.areYouSureYouWantTo'))) return;
    
    try {
      await axios.delete(`${API}/expenses/${expense.id}/receipt/${receiptId}`);
      setExistingReceipts(prev => prev.filter(r => r.id !== receiptId));
      toast.success(t('expenses.toasts.receiptDeleted'));
    } catch (error) {
      toast.error(t('expenses.toasts.deleteReceiptFailed'));
    }
  };

  // Save the expense
  const handleSave = async () => {
    // Validate required fields
    if (!formData.project_id || !formData.amount || !formData.subcategory) {
      toast.error(t('expenses.toasts.requiredFields'));
      return;
    }

    setSaving(true);
    try {
      // Update expense details
      const updateData = {
        ...formData,
        amount: parseFloat(formData.amount),
        expense_date: new Date(formData.expense_date).toISOString()
      };
      
      await axios.put(`${API}/expenses/${expense.id}`, updateData);

      // Upload new receipts
      let uploadSuccess = 0;
      let uploadFailed = 0;
      
      for (const file of pendingFiles) {
        try {
          const formDataUpload = new FormData();
          formDataUpload.append('file', file);
          await axios.post(`${API}/expenses/${expense.id}/receipt`, formDataUpload);
          uploadSuccess++;
        } catch (err) {
          console.error('Failed to upload receipt:', err);
          uploadFailed++;
        }
      }

      // Show appropriate message
      if (uploadFailed > 0) {
        toast.warning(`Expense updated. ${uploadSuccess} receipt(s) added, ${uploadFailed} failed.`);
      } else if (uploadSuccess > 0) {
        toast.success(`Expense updated with ${uploadSuccess} new receipt(s)`);
      } else {
        toast.success(t('expenses.toasts.updated'));
      }

      // Reset and close
      setPendingFiles([]);
      onSave?.();
      onOpenChange(false);
    } catch (error) {
      console.error('Error updating expense:', error);
      toast.error(error.response?.data?.detail || 'Failed to update expense');
    } finally {
      setSaving(false);
    }
  };

  // Handle dialog close
  const handleClose = () => {
    setPendingFiles([]);
    onOpenChange(false);
  };

  const totalReceiptCount = existingReceipts.length + pendingFiles.length;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('expenses.dialog.edit')}</DialogTitle>
          <DialogDescription>
            {t('expenses.dialog.editDesc')}
          </DialogDescription>
        </DialogHeader>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-4">
          {/* Project */}
          <div className="sm:col-span-2">
            <Label>{t('expenses.fields.project')}</Label>
            <Select 
              value={formData.project_id} 
              onValueChange={(v) => handleChange('project_id', v)}
            >
              <SelectTrigger>
                <SelectValue placeholder={t('expenses.ph.selectProject')} />
              </SelectTrigger>
              <SelectContent>
                {/* Non-project option at the top */}
                <SelectItem value="non-project" className="font-semibold text-gray-600 border-b">
                  📋 Non-project (General Expense)
                </SelectItem>
                {projects.map(p => (
                  <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {/* Employee Assignment */}
          <div className="sm:col-span-2">
            <Label>{t('expenses.fields.employee')}</Label>
            <Select 
              value={formData.assigned_employee_id || currentEmployeeId || ''} 
              onValueChange={(v) => handleChange('assigned_employee_id', v)}
              disabled={!formData.project_id}
            >
              <SelectTrigger data-testid="edit-expense-employee-select">
                <SelectValue placeholder={formData.project_id ? t('expenses.ph.selectEmployee') : t('expenses.ph.selectProjectFirst')} />
              </SelectTrigger>
              <SelectContent>
                {projectEmployees.length > 0 ? (
                  projectEmployees.map(emp => (
                    <SelectItem key={emp.id} value={emp.id}>
                      {emp.name} {emp.id === currentEmployeeId ? '(Me)' : ''}
                    </SelectItem>
                  ))
                ) : (
                  <SelectItem value={currentEmployeeId || 'self'}>
                    {employees?.find(e => e.id === currentEmployeeId)?.name || 'Myself'}
                  </SelectItem>
                )}
              </SelectContent>
            </Select>
            <p className="text-xs text-gray-500 mt-1">
              {isAdminOrManager 
                ? 'Change the employee this expense is tied to (for reimbursement tracking)' 
                : 'This expense is assigned to you'}
            </p>
          </div>
          
          {/* Category */}
          <div>
            <Label>{t('expenses.fields.category')}</Label>
            <Select 
              value={formData.category} 
              onValueChange={(v) => handleChange('category', v)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(expenseCategories)
                  .filter(([, cat]) => Array.isArray(cat) || cat.visible !== false)
                  .map(([key]) => (
                    <SelectItem key={key} value={key}>{getCategoryDisplayName(key)}</SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>
          
          {/* Subcategory */}
          <div>
            <Label>{t('expenses.fields.subcategory')}</Label>
            <Select 
              value={formData.subcategory} 
              onValueChange={(v) => handleChange('subcategory', v)}
            >
              <SelectTrigger>
                <SelectValue placeholder={t('expenses.ph.selectSubcategory')} />
              </SelectTrigger>
              <SelectContent>
                {getSubcategories().map(sub => (
                  <SelectItem key={sub} value={sub}>{sub}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {/* Amount */}
          <div>
            <Label>{t('expenses.fields.amount')}</Label>
            <Input
              type="number"
              step="0.01"
              value={formData.amount}
              onChange={(e) => handleChange('amount', e.target.value)}
              placeholder={t('expenses.ph.amount')}
            />
          </div>
          
          {/* Currency */}
          <div>
            <Label>{t('expenses.fields.currency')}</Label>
            <Select 
              value={formData.currency} 
              onValueChange={(v) => handleChange('currency', v)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {currencies.map(c => (
                  <SelectItem key={c} value={c}>{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {/* Date */}
          <div>
            <Label>{t('expenses.fields.date')}</Label>
            <Input
              type="date"
              value={formData.expense_date}
              onChange={(e) => handleChange('expense_date', e.target.value)}
            />
          </div>
          
          {/* Vendor */}
          <div>
            <Label>{t('expenses.fields.vendor')}</Label>
            <Input
              value={formData.vendor}
              onChange={(e) => handleChange('vendor', e.target.value)}
              placeholder={t('expenses.ph.vendor')}
            />
          </div>
          
          {/* Invoice Number */}
          <div>
            <Label>{t('expenses.fields.invoiceNumber')}</Label>
            <Input
              value={formData.invoice_number}
              onChange={(e) => handleChange('invoice_number', e.target.value)}
              placeholder={t('expenses.ph.invoice')}
            />
          </div>

          {/* Payment Method — was missing in the edit dialog (only the
              new-expense dialog had it), so editing a draft used to
              silently drop the field. */}
          <div className="sm:col-span-2">
            <Label>{t('expenses.fields.paymentMethod')}</Label>
            <Select
              value={formData.payment_method || ''}
              onValueChange={(v) => handleChange('payment_method', v)}
            >
              <SelectTrigger data-testid="edit-expense-payment-method">
                <SelectValue placeholder={t('expenses.ph.selectPaymentMethod')} />
              </SelectTrigger>
              <SelectContent>
                {paymentMethods.length > 0 ? (
                  paymentMethods.map(method => (
                    <SelectItem key={method} value={method}>{method}</SelectItem>
                  ))
                ) : (
                  ['Own Payment', 'Company Card', 'Cash Advance', 'Bank Transfer'].map(method => (
                    <SelectItem key={method} value={method}>{method}</SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          </div>
          
          {/* Receipts Section */}
          <div className="sm:col-span-2">
            <Label className="mb-2 block">{t('expenses.fields.receipts')}</Label>
            {loadingReceipts ? (
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <Loader2 className="h-4 w-4 animate-spin" />
                {t('expenses.loadingReceipts')}
              </div>
            ) : (
              <ReceiptUploader
                receipts={existingReceipts}
                pendingFiles={pendingFiles}
                onFilesChange={setPendingFiles}
                onDeleteReceipt={handleDeleteReceipt}
                onViewReceipt={handleViewReceipt}
                maxReceipts={5}
                disabled={totalReceiptCount >= 5}
              />
            )}
          </div>
          
          {/* Description */}
          <div className="sm:col-span-2">
            <Label>{t('expenses.fields.description')}</Label>
            <Textarea
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              placeholder={t('expenses.ph.description')}
              rows={3}
            />
          </div>

          {/* Link to Inventory Asset */}
          {assets.length > 0 && (
            <div className="sm:col-span-2">
              <Label>{t('expenses.fields.linkAsset')}</Label>
              <Select
                value={formData.asset_id || 'none'}
                onValueChange={(v) => handleChange('asset_id', v === 'none' ? '' : v)}
              >
                <SelectTrigger data-testid="edit-expense-asset-select">
                  <SelectValue placeholder={t('expenses.fields.noAssetLinked')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">{t('expenses.fields.noAssetLinked')}</SelectItem>
                  {assets.map(a => (
                    <SelectItem key={a.id} value={a.id}>{a.inventory_number} — {a.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500 mt-1">{t('expenses.fields.linkAssetHint')}</p>
            </div>
          )}
          
          {/* Billable to Customer */}
          {formData.project_id && formData.project_id !== 'non-project' && (
            <div className="sm:col-span-2 flex items-center space-x-2 p-3 border rounded-lg bg-blue-50/50">
              <Checkbox
                id="edit_billable_to_customer"
                checked={formData.billable_to_customer || false}
                onCheckedChange={(checked) => handleChange('billable_to_customer', checked)}
                data-testid="edit-billable-to-customer-checkbox"
              />
              <Label htmlFor="edit_billable_to_customer" className="text-sm cursor-pointer">
                Billable to customer — include on next invoice draft
              </Label>
            </div>
          )}
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={saving} data-testid="dialog-cancel-btn">
            {t('common.cancel')}
          </Button>
          <Button onClick={handleSave} disabled={saving} data-testid="dialog-save-btn">
            {saving ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                {t('expenses.actions.saving')}
              </>
            ) : (
              'Save Changes'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EditExpenseDialog;
